/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { API } from 'middleware/api';

export const ACCEPT_REQUEST = 'ACCEPT_REQUEST';
export const REJECT_REQUEST = 'REJECT_REQUEST';
export const CANCEL = 'CANCEL';
export const SEND_OFFER = 'SEND_OFFER';
export const ACCEPT_OFFER = 'ACCEPT_OFFER';
export const REJECT_OFFER = 'REJECT_OFFER';
export const UPDATE_OFFER = 'UPDATE_OFFER';

export const GET_MESSAGES_REQUEST = 'GET_MESSAGES_REQUEST';
export const GET_MESSAGES_SUCCESS = 'GET_MESSAGES_SUCCESS';
export const GET_MESSAGES_FAILURE = 'GET_MESSAGES_FAILURE';

/**
 * Create action object that is used to get inbox messages.
 *
 * @param {string} token - JWT for request authentification.
 *
 * @return {Object}
 */
export const getMessages = (token, query = {}) => ({
  [API]: {
    endpoint: '/user/discussions',
    method: 'GET',
    body: query,
    token,
    types: [GET_MESSAGES_REQUEST, GET_MESSAGES_SUCCESS, GET_MESSAGES_FAILURE],
  },
});

export const HIRED = 'HIRED';
export const REQUESTED = 'REQUESTED';
export const REQUESTED_NOT_HIRED = 'REQUESTED_NOT_HIRED';
export const GET_JOB_MESSAGES_REQUEST = 'GET_JOB_MESSAGES_REQUEST';
export const GET_JOB_MESSAGES_SUCCESS = 'GET_JOB_MESSAGES_SUCCESS';
export const GET_JOB_MESSAGES_FAILURE = 'GET_JOB_MESSAGES_FAILURE';

/**
 * Create action object that is used to get inbox messages.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} jobId - The job ID.
 * @param {Object} query - The query.
 * @param {string} query.filter - One of HIRED|REQUESTED|REQUESTED_NOT_HIRED
 *
 * @return {Object}
 */
export const getJobMessages = (token, jobId, query = {}) => ({
  [API]: {
    endpoint: `/job/${jobId}/discussions`,
    method: 'GET',
    body: query,
    token,
    types: [
      GET_JOB_MESSAGES_REQUEST,
      GET_JOB_MESSAGES_SUCCESS,
      GET_JOB_MESSAGES_FAILURE,
    ],
  },
});

export const GET_MESSAGE_REQUEST = 'GET_MESSAGE_REQUEST';
export const GET_MESSAGE_SUCCESS = 'GET_MESSAGE_SUCCESS';
export const GET_MESSAGE_FAILURE = 'GET_MESSAGE_FAILURE';

/**
 * Create action object that is used to get a single message.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} discussionId - The discussion ID.
 * @param {bool} polling - Boolean whether to set loading state or not
 *
 * @return {Object}
 */
export const getMessage = (token, discussionId, polling) => ({
  [API]: {
    endpoint: `/discussion/${discussionId}`,
    method: 'GET',
    token,
    types: [
      polling ? null : GET_MESSAGE_REQUEST,
      GET_MESSAGE_SUCCESS,
      GET_MESSAGE_FAILURE,
    ],
  },
});

export const SEND_MESSAGE_REQUEST = 'SEND_MESSAGE_REQUEST';
export const SEND_MESSAGE_SUCCESS = 'SEND_MESSAGE_SUCCESS';
export const SEND_MESSAGE_FAILURE = 'SEND_MESSAGE_FAILURE';

/**
 * Create action object that is used to send a new message.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} discussionId - The discussion ID.
 * @param {Object} message - The message object.
 * @param {string} message.message_text - A new message in the discussion.
 *
 * @return {Object}
 */
export const sendMessage = (token, discussionId, message) => ({
  [API]: {
    endpoint: `/discussion/${discussionId}/message`,
    method: 'POST',
    body: message,
    token,
    types: [SEND_MESSAGE_REQUEST, SEND_MESSAGE_SUCCESS, SEND_MESSAGE_FAILURE],
  },
});

export const DISCUSSION_ACTION_REQUEST = 'DISCUSSION_ACTION_REQUEST';
export const DISCUSSION_ACTION_SUCCESS = 'DISCUSSION_ACTION_SUCCESS';
export const DISCUSSION_ACTION_FAILURE = 'DISCUSSION_ACTION_FAILURE';

/**
 * Create action object that is used to cancel a discussion.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} discussionId - The discussion ID.
 *
 * @return {Object}
 */
export const cancelDiscussion = (token, discussionId) => ({
  [API]: {
    endpoint: `/discussion/${discussionId}/cancel`,
    method: 'POST',
    token,
    types: [
      DISCUSSION_ACTION_REQUEST,
      DISCUSSION_ACTION_SUCCESS,
      DISCUSSION_ACTION_FAILURE,
    ],
  },
});

/**
 * Create action object that is used to accept an offer.
 * (Talent only) accept a contract offer.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} discussionId - The discussion ID.
 *
 * @return {Object}
 */
export const acceptOffer = (token, discussionId) => ({
  [API]: {
    endpoint: `/discussion/${discussionId}/contract/accept`,
    method: 'POST',
    token,
    types: [
      DISCUSSION_ACTION_REQUEST,
      DISCUSSION_ACTION_SUCCESS,
      DISCUSSION_ACTION_FAILURE,
    ],
  },
});

/**
 * Create action object that is used to update the contract between the job owner and a talent.
 *
 * @param {string} token - JWT for request authentification.
 * @param {string} discussionId - The discussion ID.
 * @param {Object} offer - The offer object.
 * @param {string} offer.dates - A comma-separated list of dates
 * @param {string} offer.total_fee - Total price to be paid
 * @param {string} offer.currency - Currency of the fee.
 * @param {string} offer.has_flexible_dates
 *
 * @return {Object}
 */
export const updateOffer = (token, discussionId, offer) => ({
  [API]: {
    endpoint: `/discussion/${discussionId}/contract/update`,
    method: 'POST',
    body: offer,
    token,
    types: [
      DISCUSSION_ACTION_REQUEST,
      DISCUSSION_ACTION_SUCCESS,
      DISCUSSION_ACTION_FAILURE,
    ],
  },
});
